/**
 * Availability report data — hours only (Utilization), never points.
 *
 * The server returns classified cells; grouping, sorting, tiles and candidates run
 * in the browser, so changing Group by or a filter never refetches. Only the window
 * and "Include proposed" are server params.
 *
 * Reads go through `jsonFetch` so an API `{ error }` envelope lands in the query's
 * error state instead of being read as data.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { jsonFetch } from "@/lib/json-fetch";
import { availabilityKeys } from "@/lib/query";
import type {
  AvailabilityPayload,
  AvailabilityQuery,
  AvailabilityResourceMonthPayload,
} from "@/schemas/availability";

/** The grid refreshes in the background so Over-utilized alerts do not go stale. */
const REFRESH_MS = 60_000;

export function useAvailability(query: AvailabilityQuery) {
  return useQuery<AvailabilityPayload>({
    queryKey: availabilityKeys.report(query),
    queryFn: () => {
      const params = new URLSearchParams({
        from: query.from,
        to: query.to,
        includeProposed: String(query.includeProposed),
      });
      return jsonFetch<AvailabilityPayload>(`/api/reports/availability?${params.toString()}`);
    },
    // Stepping the window keeps the old grid on screen until the new one lands.
    placeholderData: keepPreviousData,
    staleTime: REFRESH_MS / 2,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

/** One person-month's Solutions. Lazy: pass `enabled` only while a tooltip or panel is open. */
export function useAvailabilityResourceMonth(resourceId: string, month: string, enabled: boolean) {
  return useQuery<AvailabilityResourceMonthPayload>({
    queryKey: availabilityKeys.resourceMonth(resourceId, month),
    queryFn: () => {
      const params = new URLSearchParams({ resourceId, month });
      return jsonFetch<AvailabilityResourceMonthPayload>(
        `/api/reports/availability/resource-month?${params.toString()}`,
      );
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** A 503 from the report means NOS is not configured for this environment. */
export function isNosNotConfiguredError(error: unknown): boolean {
  return error instanceof Error && /\b503\b|not configured/i.test(error.message);
}
